import * as React from 'react';
import { WithCommonProps } from '@types';
import {
  FieldContainer,
  FieldSideContent,
  FieldContent,
  FieldLabel,
  FieldInput,
} from '@components/form/field';
import { IconNames } from '@components/icon';
import { ButtonIcon } from '@components/button-icon';

export interface IInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  /* Define a string that labels the current element */
  ariaLabel?: string;
  /* Information displayed when hovering clear button */
  clearButtonTooltip?: string;
  /* Flag to indicate whether the input has an invalid value */
  isInvalid?: boolean;
  /* Flag to indicate whether the input has a border */
  hasBorder?: boolean;
  /* Flag to indicate whether the input has a caret */
  hasCaret?: boolean;
  /** Custom content to place in the left side */
  leftContent?: React.ReactNode;
  /* Indicate if the label text */
  label?: string;
  /** Custom content to place in the right side */
  rightContent?: React.ReactNode;
  /* Indicate if the label should be visible */
  showLabel?: boolean;
  /* Define if label is left aligned */
  labelLeftAligned?: boolean;
  /* Placeholder Position defaults to 'default' behavior */
  placeholderPosition?: 'default' | 'below-field';
  /**
   * Activate clear indicator and define onClick callback.
   * Helper and onClear cannot be active at the same time
   * */
  onClear?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  labelClassName?: string;
}

export type InputProps = WithCommonProps<IInputProps>;

export const Input = React.forwardRef(
  (props: InputProps, ref: React.ForwardedRef<HTMLDivElement>): JSX.Element => {
    const {
      ariaLabel,
      className,
      labelClassName,
      clearButtonTooltip,
      disabled = false,
      label,
      name,
      isInvalid,
      hasBorder,
      hasCaret,
      leftContent,
      readOnly = false,
      rightContent,
      showLabel = true,
      labelLeftAligned = false,
      type,
      value = '',
      onBlur,
      onClear,
      onFocus,
      ...rest
    } = props;

    if (rest.placeholderPosition === 'below-field') {
      delete rest.placeholder;
    }

    const [isFocused, setIsFocused] = React.useState(false);

    const onBlurInput = (event: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(event);
      setIsFocused(false);
    };

    const onFocusInput = (event: React.FocusEvent<HTMLInputElement>) => {
      onFocus?.(event);
      setIsFocused(true);
    };

    return (
      <FieldContainer
        className={className}
        isFocused={isFocused}
        isInvalid={isInvalid}
        hasBorder={hasBorder}
        hasCaret={hasCaret}
        disabled={disabled}
        readOnly={readOnly}
        ref={ref}
      >
        {leftContent && <FieldSideContent>{leftContent}</FieldSideContent>}
        <FieldContent>
          {showLabel && label && label !== '' ? (
            <FieldLabel
              aria-label={ariaLabel}
              htmlFor={name}
              hasFocusedValue={isFocused || value !== '' || type === 'date'}
              isDisabled={disabled}
              leftAligned={labelLeftAligned}
              className={labelClassName}
            >
              {label}
            </FieldLabel>
          ) : null}
          <FieldInput
            disabled={disabled}
            isInvalid={isInvalid}
            name={name}
            readOnly={readOnly}
            showLabel={showLabel && !!label}
            type={type}
            value={value}
            onBlur={onBlurInput}
            onFocus={onFocusInput}
            {...rest}
          />
        </FieldContent>

        {(rightContent || onClear) && (
          <FieldSideContent align="right">
            {onClear && value && (
              <ButtonIcon
                ariaDescribedby={clearButtonTooltip || ''}
                icon={IconNames.CIRCLE_X}
                iconSolid={true}
                iconSize={'sm'}
                variant={'text'}
                onClick={onClear}
              >
                {clearButtonTooltip}
              </ButtonIcon>
            )}
            {rightContent}
          </FieldSideContent>
        )}
      </FieldContainer>
    );
  }
);

Input.displayName = 'Input';
